
import Vue from 'vue';
import { Icon } from '@descomplica/vue-components';
import {
  Assessment,
  ASSESSMENT_RESULT_STATUS,
  ASSESSMENT_STATUS,
  ASSESSMENT_TYPE,
} from '@/models/greathall/assessment.model';
import i18n from '@/i18n';
import dayjs from 'dayjs';
import { TranslateResult } from 'vue-i18n';
import PostGradAssessmentsCardRowSkeleton from '@/components/skeletons/PostGradAssessmentsCardRowSkeleton.vue';
import { POSTGRAD_ASSESSMENTS_CARD_ROW_ICON_NAMES } from '@/components/cards/assessments/utils';
import { CourseModel } from '@descomplica/dex-models';
import { WarningLevels } from '@/enum/postgradStudentDocuments.enum';
import { StudentDocumentsStatusInfo } from '@/helpers/postgradStudentDocuments/interfaces';

interface Data {
  showSkeleton: boolean;
}

export default Vue.extend({
  components: {
    Icon,
    PostGradAssessmentsCardRowSkeleton,
  },
  props: {
    assessment: {
      type: Object as Vue.PropType<Assessment>,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    isCourseWithTcc: {
      type: Boolean,
      required: true,
    },
  },
  data(): Data {
    return {
      showSkeleton: true,
    };
  },
  computed: {
    iconName(): string {
      return this.userHasPendingDocuments
        ? POSTGRAD_ASSESSMENTS_CARD_ROW_ICON_NAMES.unavailable
        : POSTGRAD_ASSESSMENTS_CARD_ROW_ICON_NAMES[this.assessment.assessmentStatusByUser];
    },
    iconClass(): string {
      return this.userHasPendingDocuments
        ? `icon__unavailable`
        : `icon__${this.assessment.assessmentStatusByUser}`;
    },
    iconBoxClass(): string {
      return this.userHasPendingDocuments
        ? `icon_box__unavailable`
        : `icon_box__${this.assessment.assessmentStatusByUser}`;
    },
    title(): TranslateResult | string {
      const { title, assessmentType } = this.assessment;

      if (this.isCourseWithTcc) return title;
      return i18n.t(`yourAssessments.rowTitles.${assessmentType}`, { title });
    },
    moduleInfoText(): TranslateResult {
      if (this.userHasPendingDocuments) return i18n.t('yourAssessments.blocked');

      const {
        daysToRetry,
        assessmentType,
        daysUntilAvailable,
        currentRelatedCourse,
        assessmentStatusByUser: status,
      } = this.assessment;

      if (status === ASSESSMENT_STATUS.AVAILABLE) {
        return i18n.t('yourAssessments.available');
      }

      if (status === ASSESSMENT_STATUS.WAITING_GRADING) {
        if (assessmentType === ASSESSMENT_TYPE.DEX) {
          return i18n.t('yourAssessments.waitingGradingDex');
        }
        return i18n.t('yourAssessments.waitingGrading');
      }

      if (status === ASSESSMENT_STATUS.UNAVAILABLE) {
        if (!currentRelatedCourse.isAvailable) {
          return i18n.t('yourAssessments.unavailableForCourse');
        }
        if (daysUntilAvailable === 0) {
          return i18n.t('yourAssessments.availableWhen');
        }
        if (daysUntilAvailable > 0) {
          const availableAt = dayjs().add(daysUntilAvailable, 'days').format('DD/MM/YYYY');
          return i18n.t('yourAssessments.availableAt', { availableAt });
        }
      }

      const {
        createdAt,
        gradingDate,
        status: statusAssessmentResult,
      } = this.assessment.assessmentResultsByUser[0];

      if (status === ASSESSMENT_STATUS.PASSED) {
        const takenAt = dayjs(createdAt).format('DD/MM/YYYY');
        return i18n.t('yourAssessments.takenAt', { takenAt });
      }

      if (status === ASSESSMENT_STATUS.RETRY) {
        const dateAvailable = dayjs(gradingDate).add(daysToRetry, 'days');
        const isAvailable = dateAvailable.unix() > dayjs().unix();
        const availableAgainAt = dateAvailable.format('DD/MM/YYYY');

        if (isAvailable) {
          return i18n.t('yourAssessments.availableAgainAt', { availableAgainAt });
        }
        return i18n.t('yourAssessments.available');
      }
      if (status === ASSESSMENT_STATUS.TCC_IN_PROGRESS) {
        const failedTCCs = this.assessment.assessmentResultsByUser.filter(
          (assessment) => assessment.status === ASSESSMENT_RESULT_STATUS.FAILED,
        ).length;

        const tccAttempt =
          failedTCCs + Number(statusAssessmentResult !== ASSESSMENT_RESULT_STATUS.FAILED);

        const mountStatus = (rowStatus: TranslateResult) =>
          tccAttempt > 1
            ? `${i18n.t('yourAssessments.attempt')} ${tccAttempt}: ${String(
                rowStatus,
              ).toLowerCase()}`
            : rowStatus;

        if (statusAssessmentResult === ASSESSMENT_RESULT_STATUS.TCC_THEME_PENDING_APPROVAL) {
          return mountStatus(i18n.t('yourAssessments.tccThemePendingApproval'));
        }
        if (statusAssessmentResult === ASSESSMENT_RESULT_STATUS.TCC_THEME_APPROVED) {
          return mountStatus(i18n.t('yourAssessments.tccThemeApproved'));
        }
        if (statusAssessmentResult === ASSESSMENT_RESULT_STATUS.TCC_THEME_REJECTED) {
          return mountStatus(i18n.t('yourAssessments.tccThemeRejected'));
        }
        if (statusAssessmentResult === ASSESSMENT_RESULT_STATUS.PARTIAL_TCC_PENDING_REVIEW) {
          return mountStatus(i18n.t('yourAssessments.partialTCCPendingReview'));
        }
        if (statusAssessmentResult === ASSESSMENT_RESULT_STATUS.PARTIAL_TCC_APPROVED) {
          return mountStatus(i18n.t('yourAssessments.partialTCCApproved'));
        }
        if (statusAssessmentResult === ASSESSMENT_RESULT_STATUS.PARTIAL_TCC_REJECTED) {
          return mountStatus(i18n.t('yourAssessments.partialTCCRejected'));
        }
        if (statusAssessmentResult === ASSESSMENT_RESULT_STATUS.NONE) {
          return mountStatus(i18n.t('yourAssessments.available'));
        }
        if (statusAssessmentResult === ASSESSMENT_RESULT_STATUS.WAITING_GRADING) {
          return mountStatus(i18n.t('yourAssessments.tccWaitingGrading'));
        }
        if (statusAssessmentResult === ASSESSMENT_RESULT_STATUS.WAITING_REVALUATION) {
          return mountStatus(i18n.t('yourAssessments.waitingRevaluation'));
        }
        if (
          [
            ASSESSMENT_RESULT_STATUS.FAILED_ELEGIBLE_FOR_REVALUATION,
            ASSESSMENT_RESULT_STATUS.FAILED,
          ].includes(statusAssessmentResult)
        ) {
          return mountStatus(i18n.t('yourAssessments.tccFailed'));
        }
      }

      return '';
    },
    isClickable(): boolean {
      const {
        assessmentType,
        assessmentStatusByUser,
        currentRelatedCourse: { isAvailable },
      } = this.assessment;

      return (
        isAvailable &&
        (assessmentStatusByUser !== ASSESSMENT_STATUS.WAITING_GRADING ||
          assessmentType === ASSESSMENT_TYPE.DEX)
      );
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    userDocumentsStatus(): StudentDocumentsStatusInfo | null {
      return this.$store.getters.studentDocumentsStatusByCourseId(this.currentCourse.id);
    },
    userHasPendingDocuments(): boolean {
      if (!this.userDocumentsStatus) {
        return false;
      }

      return this.userDocumentsStatus.delayWarningLevel === WarningLevels.FINAL;
    },
    isAssessmentUnavailable(): boolean {
      return (
        this.assessment.assessmentStatusByUser === 'unavailable' || this.userHasPendingDocuments
      );
    },
  },
  watch: {
    isLoading: {
      immediate: true,
      handler(newValue) {
        this.showSkeleton = newValue;
      },
    },
  },
});
